import { useState, useEffect } from 'react';
import { StatisticsWidget } from '../components/StatisticsWidget';
import {
  Search,
  XCircle,
  RotateCcw,
  Eye,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

interface Certificate {
  id: string;
  certificate_number: string;
  title: string;
  ects_credits: number;
  eqf_level: number;
  status: 'active' | 'revoked';
  issue_date: string;
  valid_until: string | null;
  view_count: number;
  verification_count: number;
  revoked_at: string | null;
  revoked_by: string | null;
  revocation_reason: string | null;
  candidate_name: string;
  member_name: string;
  association_name: string;
}

export function AdminDashboardPage() {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'revoked'>('all');
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedCert, setSelectedCert] = useState<Certificate | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeLoading, setRevokeLoading] = useState(false);

  useEffect(() => {
    fetchCertificates();
  }, [statusFilter]);

  async function fetchCertificates() {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      params.append('limit', '100');

      const res = await fetch(`/api/admin/certificates?${params.toString()}`, {
        credentials: 'include'
      });

      if (!res.ok) {
        throw new Error('Kunne ikke hente attester');
      }

      const data = await res.json();
      setCertificates(data.certificates || []);
    } catch (err) {
      console.error('Fetch certificates error:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleRevoke() {
    if (!selectedCert || !revokeReason.trim()) {
      alert('Vennligst oppgi en grunn for tilbakekalling');
      return;
    }

    try {
      setRevokeLoading(true);
      const res = await fetch(`/api/admin/certificates/${selectedCert.id}/revoke`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: revokeReason,
          admin_user_id: 'admin' // TODO: Get from session
        })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Kunne ikke tilbakekalle attest');
      }

      alert('Attest tilbakekalt');
      setShowRevokeModal(false);
      setSelectedCert(null);
      setRevokeReason('');
      fetchCertificates();
    } catch (err: any) {
      alert('Feil: ' + err.message);
    } finally {
      setRevokeLoading(false);
    }
  }

  async function handleReinstate(cert: Certificate) {
    const reason = prompt('Oppgi grunn for gjeninnføring:');
    if (!reason) return;

    try {
      const res = await fetch(`/api/admin/certificates/${cert.id}/reinstate`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason,
          admin_user_id: 'admin' // TODO: Get from session
        })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Kunne ikke gjeninnføre attest');
      }

      alert('Attest gjeninnført');
      fetchCertificates();
    } catch (err: any) {
      alert('Feil: ' + err.message);
    }
  }

  const filteredCerts = certificates.filter((cert) =>
    cert.certificate_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.candidate_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-kf-gray-900">Admin Dashboard</h1>
        <div className="px-4 py-2 bg-kf-orange-100 border border-kf-orange-300 rounded-lg">
          <p className="text-sm font-semibold text-kf-orange-800">
            ⚠️ Merk: Denne siden er for demo. Integrer i hovedadmin-panelet.
          </p>
        </div>
      </div>

      {/* Statistics Widget */}
      <StatisticsWidget />

      {/* Certificate Management */}
      <div className="card">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-kf-gray-900 mb-4">🔧 Attesthåndtering</h2>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-kf-gray-400" />
              <input
                type="text"
                placeholder="Søk etter attestnummer, tittel eller kandidat..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-orange focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-4 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-orange focus:border-transparent"
            >
              <option value="all">Alle statuser</option>
              <option value="active">Aktive</option>
              <option value="revoked">Tilbakekalt</option>
            </select>
          </div>
        </div>

        {/* Certificates Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kf-orange mx-auto"></div>
            <p className="mt-4 text-kf-gray-600">Laster attester...</p>
          </div>
        ) : filteredCerts.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-16 h-16 text-kf-gray-400 mx-auto mb-4" />
            <p className="text-kf-gray-600">Ingen attester funnet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-kf-gray-200">
                  <th className="text-left py-3 px-2 text-sm font-semibold text-kf-gray-700">Attestnr</th>
                  <th className="text-left py-3 px-2 text-sm font-semibold text-kf-gray-700">Tittel</th>
                  <th className="text-left py-3 px-2 text-sm font-semibold text-kf-gray-700">Kandidat</th>
                  <th className="text-left py-3 px-2 text-sm font-semibold text-kf-gray-700">Status</th>
                  <th className="text-left py-3 px-2 text-sm font-semibold text-kf-gray-700">Utstedt</th>
                  <th className="text-center py-3 px-2 text-sm font-semibold text-kf-gray-700">
                    <Eye className="w-4 h-4 mx-auto" />
                  </th>
                  <th className="text-right py-3 px-2 text-sm font-semibold text-kf-gray-700">Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {filteredCerts.map((cert) => (
                  <tr key={cert.id} className="border-b border-kf-gray-100 hover:bg-kf-gray-50">
                    <td className="py-3 px-2">
                      <p className="font-mono text-sm">{cert.certificate_number}</p>
                    </td>
                    <td className="py-3 px-2">
                      <p className="font-semibold text-kf-gray-900">{cert.title}</p>
                      <p className="text-xs text-kf-gray-500">{cert.member_name}</p>
                    </td>
                    <td className="py-3 px-2">
                      <p className="text-sm text-kf-gray-900">{cert.candidate_name}</p>
                    </td>
                    <td className="py-3 px-2">
                      {cert.status === 'active' ? (
                        <span className="px-2 py-1 bg-kf-blue-100 text-kf-blue-800 text-xs font-semibold rounded-full flex items-center gap-1 w-fit">
                          <CheckCircle className="w-3 h-3" />
                          Aktiv
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-kf-error-100 text-kf-error-800 text-xs font-semibold rounded-full flex items-center gap-1 w-fit">
                          <XCircle className="w-3 h-3" />
                          Tilbakekalt
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-sm text-kf-gray-600">
                      {new Date(cert.issue_date).toLocaleDateString('no-NO')}
                    </td>
                    <td className="py-3 px-2 text-center">
                      <div className="flex flex-col items-center">
                        <p className="text-sm font-semibold text-kf-gray-900">{cert.view_count}</p>
                        <p className="text-xs text-kf-gray-500">{cert.verification_count} verif.</p>
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex items-center justify-end gap-2">
                        {cert.status === 'active' ? (
                          <button
                            onClick={() => {
                              setSelectedCert(cert);
                              setShowRevokeModal(true);
                            }}
                            className="p-2 hover:bg-kf-error-100 text-kf-error-600 rounded-lg transition-colors"
                            title="Tilbakekall attest"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReinstate(cert)}
                            className="p-2 hover:bg-kf-blue-100 text-kf-blue-600 rounded-lg transition-colors"
                            title="Gjeninnfør attest"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Revoke Modal */}
      {showRevokeModal && selectedCert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-kf-error-100 rounded-lg">
                <XCircle className="w-6 h-6 text-kf-error-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-kf-gray-900 mb-1">Tilbakekall attest</h3>
                <p className="text-sm text-kf-gray-600 mb-4">
                  Er du sikker på at du vil tilbakekalle denne attesten?
                </p>
                <div className="p-3 bg-kf-gray-50 rounded-lg mb-4">
                  <p className="text-sm font-mono text-kf-gray-700">{selectedCert.certificate_number}</p>
                  <p className="text-sm font-semibold text-kf-gray-900">{selectedCert.title}</p>
                  <p className="text-xs text-kf-gray-600">{selectedCert.candidate_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-kf-gray-700 mb-2">
                    Grunn for tilbakekalling *
                  </label>
                  <textarea
                    value={revokeReason}
                    onChange={(e) => setRevokeReason(e.target.value)}
                    placeholder="Forklar hvorfor attesten tilbakekalles..."
                    rows={4}
                    className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-error-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRevokeModal(false);
                  setSelectedCert(null);
                  setRevokeReason('');
                }}
                disabled={revokeLoading}
                className="flex-1 btn-secondary"
              >
                Avbryt
              </button>
              <button
                onClick={handleRevoke}
                disabled={revokeLoading || !revokeReason.trim()}
                className="flex-1 bg-kf-error-600 text-white hover:bg-kf-error-700 py-2 px-4 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {revokeLoading ? 'Tilbakekaller...' : 'Tilbakekall attest'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
